import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import seoConfig from '../config/seoConfig';

// Page-specific SEO data
const pageSEO = {
  '/': {
    title: 'Professional Graphic Design Services | Faheem Graphics',
    description: 'Transform your brand with professional graphic design services. Logo design, branding, social media graphics, and print materials. Get a free consultation today.',
    keywords: 'graphic design, logo design, branding, social media graphics, print design, pakistan',
    image: '/images/home-og.jpg',
    type: 'website'
  },
  '/about': {
    title: 'About Faheem Graphics | Our Story & Mission',
    description: 'Learn about Faheem Graphics - your trusted partner for creative design solutions. Discover our mission, values, and commitment to excellence.',
    keywords: 'about us, graphic design company, creative agency, design team',
    image: '/images/about-og.png',
    type: 'website'
  },
  '/services': {
    title: 'Professional Design Services | Faheem Graphics',
    description: 'Explore our comprehensive design services including logo design, branding, web design, packaging, and digital marketing materials.',
    keywords: 'design services, logo design, branding services, web design, packaging design',
    image: '/images/services-og.png',
    type: 'website'
  },
  '/portfolio': {
    title: 'Design Portfolio | Faheem Graphics Projects',
    description: 'Browse our portfolio of successful design projects. See how we\'ve helped businesses transform their brand identity.',
    keywords: 'design portfolio, graphic design work, logo examples, branding projects',
    image: '/images/portfolio-og.png',
    type: 'website'
  },
  '/contact': {
    title: 'Contact Faheem Graphics | Get a Free Quote',
    description: 'Ready to start your design project? Contact Faheem Graphics for a free consultation and quote. We reply within 24 hours.',
    keywords: 'contact graphic designer, design quote, graphic design consultation',
    image: '/images/contact-og.png',
    type: 'website'
  }
};

function SEO({ customData = {} }) {
  const location = useLocation();
  const currentPageSEO = pageSEO[location.pathname] || pageSEO['/'];
  const { siteName, siteUrl, defaultImage, twitterHandle } = seoConfig;

  // Merge page SEO with custom data if provided
  const seo = {
    ...currentPageSEO,
    ...customData,
    title: customData.title || currentPageSEO.title,
    description: customData.description || currentPageSEO.description
  };

  const canonicalUrl = `${siteUrl}${location.pathname}`;
  const imageUrl = seo.image ? `${siteUrl}${seo.image}` : `${siteUrl}${defaultImage}`;

  // Generate breadcrumb schema
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: siteUrl
      }
    ]
  };

  // Add breadcrumb items based on path
  const pathSegments = location.pathname.split('/').filter(segment => segment);
  pathSegments.forEach((segment, index) => {
    breadcrumbSchema.itemListElement.push({
      '@type': 'ListItem',
      position: index + 2,
      name: segment.charAt(0).toUpperCase() + segment.slice(1),
      item: `${siteUrl}/${pathSegments.slice(0, index + 1).join('/')}`
    });
  });

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <html lang="en" />
      <title>{seo.title}</title>
      <meta name="description" content={seo.description} />
      <meta name="keywords" content={seo.keywords} />
      <link rel="canonical" href={canonicalUrl} />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <meta name="robots" content="index, follow" />
      <meta name="author" content={siteName} />
      
      {/* Favicon */}
      <link rel="icon" type="image/x-icon" href={seoConfig.favicon} />
      <link rel="apple-touch-icon" href={seoConfig.favicon} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={seo.type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:site_name" content={siteName} />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={twitterHandle} />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={imageUrl} />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>
    </Helmet>
  );
}

export default SEO;